import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Indicator } from 'client/voladeum/indicators/types';

import { indicator_context_initial_state } from './defaults';
import { IndicatorContextProps } from './types';

const Context = createContext<IndicatorContextProps>({ ...indicator_context_initial_state });

export const IndicatorContext: FC<PropsWithChildren> = ({ children }) => {
  const [context, setContext] = useState<IndicatorContextProps>({
    ...indicator_context_initial_state,
  });

  const setIndicator = useCallback((indicator: Indicator) => {
    setContext((prev) => ({ ...prev, indicator }));
  }, []);

  const updateIndicator = useCallback((indicator: Partial<Indicator>) => {
    setContext((prev) => {
      return { ...prev, indicator: { ...prev.indicator, ...indicator } };
    });
  }, []);

  const setIndicators = useCallback((indicators: Indicator[]) => {
    setContext((prev) => ({ ...prev, indicators }));
  }, []);

  const updateIndicators = useCallback((indicator: Indicator) => {
    setContext((prev) => {
      const indicators = [...prev.indicators];
      const index = indicators.findIndex((i) => i.uuid === indicator.uuid);

      if (index !== -1) {
        indicators.splice(index, 1);
      } else {
        indicators.push(indicator);
      }
      return { ...prev, indicators };
    });
  }, []);

  const value = useMemo(
    () => ({ ...context, setIndicators, updateIndicators, setIndicator, updateIndicator }),
    [context, setIndicators, updateIndicators, setIndicator, updateIndicator]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useIndicatorContext = () => useContext(Context);
